/**
 * TikTok 尺码表测量列表头：平台英文名 → 映射库/DeepSeek 中文，展示「中文 / English」。
 * 提交平台仍用原始英文 name。
 */

import { glossaryAPI } from '@/utils/api';

export const TIKTOK_SIZE_CHART_MEASURE_SCOPE = 'tiktok_size_chart_measure';

/** @type {Map<string, { nameZh: string, label: string }>} */
const memoryCache = new Map();

/** 进行中的批量请求（同 key 去重） */
const inflight = new Map();

function cacheKey(id, name) {
  return `${String(id || '').trim()}|${String(name || '').trim()}`;
}

function hasCjk(text = '') {
  return /[\u3400-\u9fff]/.test(String(text || ''));
}

/** 同步展示：优先已解析的中文映射 */
export function formatTiktokSizeChartMeasureLabel({ id, name, nameZh } = {}) {
  const enOrRaw = String(name || '').trim();
  const cached = memoryCache.get(cacheKey(id, enOrRaw));
  const zh = String(nameZh || cached?.nameZh || '').trim();
  if (cached?.label && !nameZh) return cached.label;
  if (!zh && !enOrRaw) return String(id || '').trim() || '—';
  if (!zh) return enOrRaw || String(id || '');
  if (!enOrRaw || hasCjk(enOrRaw) || zh === enOrRaw) return zh;
  return `${zh} / ${enOrRaw}`;
}

/**
 * 批量解析测量项中文名：先读内存缓存 → 调后端映射库（未命中则 DeepSeek 翻译落库）。
 * @param {Array<{ id?: string, name?: string }>} items
 * @returns {Promise<Map<string, { nameZh: string, label: string }>>}
 */
export async function resolveTiktokSizeChartMeasureLabels(items = []) {
  const list = (Array.isArray(items) ? items : [])
    .map((it) => ({
      id: String(it?.id || '').trim(),
      name: String(it?.name || '').trim(),
    }))
    .filter((it) => it.id || it.name);

  const result = new Map();
  const pending = [];
  list.forEach((it) => {
    const key = cacheKey(it.id, it.name);
    const hit = memoryCache.get(key);
    if (hit) {
      result.set(key, hit);
      return;
    }
    if (hasCjk(it.name)) {
      const row = { nameZh: it.name, label: it.name };
      memoryCache.set(key, row);
      result.set(key, row);
      return;
    }
    pending.push(it);
  });

  if (!pending.length) return result;

  const batchKey = pending.map((it) => cacheKey(it.id, it.name)).sort().join(';;');
  let promise = inflight.get(batchKey);
  if (!promise) {
    promise = glossaryAPI.resolveTermTranslations({
      scope: TIKTOK_SIZE_CHART_MEASURE_SCOPE,
      targetLang: 'zh',
      items: pending,
    }).finally(() => {
      inflight.delete(batchKey);
    });
    inflight.set(batchKey, promise);
  }

  const res = await promise;
  const rows = Array.isArray(res?.items) ? res.items : [];
  rows.forEach((row) => {
    const id = String(row?.id || '').trim();
    const name = String(row?.name || '').trim();
    const nameZh = String(row?.nameZh || '').trim();
    const label = String(row?.label || '').trim()
      || formatTiktokSizeChartMeasureLabel({ id, name, nameZh });
    const key = cacheKey(id, name);
    const entry = { nameZh, label };
    memoryCache.set(key, entry);
    result.set(key, entry);
    // 兼容只按 id / 只按 name 查
    if (id) memoryCache.set(cacheKey(id, ''), entry);
    if (name) memoryCache.set(cacheKey('', name), entry);
  });

  // 接口未返回的项：仍用英文兜底，避免表头空白
  pending.forEach((it) => {
    const key = cacheKey(it.id, it.name);
    if (!result.has(key)) {
      const entry = { nameZh: '', label: it.name || it.id || '—' };
      memoryCache.set(key, entry);
      result.set(key, entry);
    }
  });

  return result;
}

/** 按 id/name 取已缓存展示文案 */
export function getCachedTiktokSizeChartMeasureLabel({ id, name } = {}) {
  const key = cacheKey(id, name);
  return memoryCache.get(key)?.label
    || memoryCache.get(cacheKey(id, ''))?.label
    || memoryCache.get(cacheKey('', name))?.label
    || formatTiktokSizeChartMeasureLabel({ id, name });
}
